/**
 * Small search-hit building blocks shared by the inline row and the pane
 * (search-result-disclosure 03). Type labels are the one place the container
 * `kind` / item `category` ambiguity is resolved for display: item categories
 * are translated, container kinds print raw.
 */

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useT, type Translate } from "../../shared/i18n";
import { itemCategoryLabel } from "../../shared/components/CategoryChip";
import type { HitContents, SearchHit } from "./searchHits";

const CHIP_SX = { height: 22, fontSize: "0.72rem" } as const;

/** The display label of a hit's type chip. */
export function hitTypeLabel(hit: SearchHit, t: Translate): string {
  if (hit.result.type !== "item") return hit.type;
  return itemCategoryLabel(hit.type, t);
}

/** Owner/type/tag chips. */
export function HitMetaChips({ hit }: { hit: SearchHit }) {
  const t = useT();
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
      <Chip
        size="small"
        variant="outlined"
        label={hit.owner || t("filterUnassigned")}
        sx={CHIP_SX}
      />
      <Chip size="small" label={hitTypeLabel(hit, t)} sx={CHIP_SX} />
      {hit.tags.length > 0 && (
        <Chip
          size="small"
          variant="outlined"
          label={hit.tags.join(" · ")}
          sx={{ ...CHIP_SX, maxWidth: 260 }}
        />
      )}
    </Stack>
  );
}

/** The level-2 property grid. */
export function HitFacts({ hit }: { hit: SearchHit }) {
  const t = useT();
  return (
    <Box
      data-testid="hit-facts"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
        gap: 1,
      }}
    >
      <Fact label={t("filterOwner")} value={hit.owner || t("filterUnassigned")} />
      <Fact label={t("filterType")} value={hitTypeLabel(hit, t)} />
      <Fact
        label={t("filterTag")}
        value={hit.tags.length > 0 ? hit.tags.join(", ") : "—"}
      />
    </Box>
  );
}

/** "What's inside" for a container hit; nothing for items without contents. */
export function HitContents({
  contents,
  limit = 4,
}: {
  contents: readonly HitContents[];
  limit?: number;
}) {
  const t = useT();
  if (contents.length === 0) return null;
  const shown = contents.slice(0, limit);
  const overflow = contents.length - shown.length;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
        {t("searchContents")}
      </Typography>
      <Stack component="ul" sx={{ m: 0, pl: 2.25, py: 0 }} spacing={0.25}>
        {shown.map((entry) => (
          <Typography key={entry.name} component="li" variant="body2">
            {entry.name}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.75 }}
            >
              ×{entry.quantity}
            </Typography>
          </Typography>
        ))}
        {overflow > 0 && (
          <Typography component="li" variant="caption" color="text.secondary">
            {t("searchMoreItems", { count: String(overflow) })}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
